import { Button, mergeClasses, Tooltip } from '@fluentui/react-components'
import { CommentMultiple20Filled, CommentMultiple20Regular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { useContextSourceBadgeStyles } from './contextSourceBadge.styles'

interface CommentsContextBadgeProps {
  isActive: boolean
  onChange: (active: boolean) => void
  // No comments exist in the current document, so there is nothing to include.
  disabled?: boolean
}

/**
 * Badge that lets the user independently include existing document comments as
 * context for the next chat send. Separate from review/comment mode:
 *
 * - Review mode off + badge on → comments travel to the AI as read-only context
 *   (AI can reference them in its reply, but won't insert new comments into Word).
 * - Review mode on + badge off → AI reviews the document but sees no existing threads.
 * - Review mode on + badge on (default) → existing threads are sent; AI can reply to them.
 *
 * Auto-activates when review/comment mode turns on. The user can still toggle
 * it independently in either direction.
 *
 * Shows as disabled when the document has no comments — there is nothing to
 * include. See useDocumentComments for how that state is tracked.
 *
 * Only rendered when REVIEW_MODE_ENABLED, gated in ContextBar alongside the
 * review/comment mode buttons.
 */
export const CommentsContextBadge = ({
  isActive,
  onChange,
  disabled = false,
}: CommentsContextBadgeProps) => {
  const classes = useContextSourceBadgeStyles()
  const { t } = useTranslation()

  // Disabled means there is nothing to include - never show as pressed/active.
  const showActive = isActive && !disabled

  const tooltipContent = disabled
    ? t('contextBar.commentsDisabled')
    : showActive
      ? t('contextBar.commentsActive')
      : t('contextBar.commentsInactive')

  const visualClass = disabled ? classes.disabled : showActive ? classes.active : classes.inactive

  const badge = (
    <Button
      className={mergeClasses(classes.badge, visualClass)}
      appearance="subtle"
      size="small"
      shape="circular"
      disabled={disabled}
      icon={
        showActive ? (
          <CommentMultiple20Filled aria-hidden={true} />
        ) : (
          <CommentMultiple20Regular aria-hidden={true} />
        )
      }
      aria-label={tooltipContent}
      aria-pressed={showActive}
      onClick={() => onChange(!isActive)}
    />
  )

  // Disabled buttons do not receive pointer events - wrap so the Figma tooltip still shows.
  return (
    <Tooltip content={tooltipContent} relationship="label" appearance="inverted">
      {disabled ? <span className={classes.tooltipAnchor}>{badge}</span> : badge}
    </Tooltip>
  )
}
